import type { Batch, BatchListRow } from '@ctcells/contract'

import { dateTimeText, durationText, orDash } from '../screens/format.ts'

/**
 * `Batch`(계약) → `BatchListRow`(화면) 변환.
 *
 * 🔴 **화면이 계산하지 않는 값만 여기서 만든다.** 표시 문자열(시각 포맷 · 대시)이 전부이고,
 *    버튼 활성에 쓰이는 값은 **한 글자도 만들지 않는다** — `allowed_commands` 는 그대로 흐른다.
 *
 * ## ⚠ 「진행」 칸은 없다 (2026-09-30)
 *
 * 목록의 「진행」 열이 빠졌다(eric 「진행 중 탭 컬럼도.. 이력 컬럼에서 소요 시간 빼고 동일하게」). 진행은 REST 에 없고
 * (BR-09) 구독 `batch_progress` 에만 있어, 이 변환이 구독 값을 받던 까닭은 그 열 하나였다 — 인자째 걷었다.
 * 진행 표기는 상시 작업 줄이 `stripStepCount` 로 만든다. 🔴 되살릴 때 `progress_text: '—'` 를 박지 마라
 * (2026-09-22 까지 그래서 언제나 대시였다 · eric 「컬럼들 봐봐」).
 *
 * ## 🔴 레시피 이름 · 담당 · 시료 수는 응답 칸이다 (2026-09-26)
 *
 * `Batch` 에 `recipe_name` · `created_by` · `sample_count` 가 생겼다(실측 50/50 · `app/views/batches.py`
 * `_dto`). 종전에는 이름을 `/recipes` 를 따로 받아 `recipe_id` 로 붙였고, 담당·시료 수는
 * 「응답에 없다」로 열을 뺐었다. **별도 조회를 걷었다 — 되살리지 마라.**
 */

export function toBatchListRow(batch: Batch): BatchListRow {
  return {
    batch_id: batch.batch_id,
    // 🔴 응답 칸이다(지금 이름). `null` = 서버가 레시피 행을 못 찾았다 → 추적할 수 있게 id 로
    recipe_name: batch.recipe_name ?? batch.recipe_id,
    // 🔴 **응답에 있는 값이다.** 이름 옆에 `v1` 로 붙는다 — 작업 줄·작업 상세와 같은 모양
    recipe_version: batch.recipe_version,
    status: batch.status,
    // 🔴 계정 ID 그대로다 — 이름이 아니다. `null` 은 「기록 안 됨」이고 화면이 `—` 로 그린다
    created_by: batch.created_by,
    sample_count: batch.sample_count,
    // 🔴 **계약에 없는데 서버가 더 주는 값**이다. 버리지 마라 — 사유는 `BatchListRow` 주석
    halt_reason: batch.halt_reason ?? null,
    // 🔴 응답에 늘 있다. 시작 전 배치는 시작 시각이 `—` 라 이 칸이 유일한 시간 단서다
    created_text: dateTimeText(batch.created_at),
    started_text: orDash(batch.started_at === null ? null : dateTimeText(batch.started_at)),
    // 🔴 종착 시각 — `aborted` 도 채워진다(서버가 종착 전이에 쓴다). 종착 전이면 `—`
    completed_text: orDash(batch.completed_at === null ? null : dateTimeText(batch.completed_at)),
    // 🔴 **응답 두 칸에서 결정된다** — 진행 중이면 `completed_at` 이 없어 `—` 다(시계를 돌리지 않는다)
    duration_text: durationText(batch.started_at, batch.completed_at),
    // 🔴 정렬용 원값 — 화면에 그리지 않는다(표기는 연도가 없어 글자 순이 시각 순이 아니다)
    created_at: batch.created_at,
    started_at: batch.started_at,
    completed_at: batch.completed_at,
  }
}
